"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Check,
  ChevronDown,
  Plus,
  X,
  ChevronLeft,
  Building2,
  Palette,
  Smartphone,
  Rocket,
} from "lucide-react";
import { ChromePicker } from "react-color";
import { SketchPicker } from "react-color";
import {
  useCreateCampaignMutation,
  useCreateTenantMutation,
} from "@/store/slices/apiSlice";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UploadsImagesWithPreview } from "@/components/common/Dropzone";
import SuccessModal from "@/components/tenants/SuccessModal";
import CustomSelect from "@/components/tenants/CustomSelect";
import ColorInput from "@/components/tenants/ColorInput";
import CampaignAccessSelect from "@/components/tenants/CampaignAccessSelect";
import {
  useGetPresignedURLQuery,
  useUpdateTenantBrandingMutation,
} from "@/store/slices/apiSlice";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import AddExperienceDialog from "@/components/dialogs/tenants/experiences/AddExperienceDialog";
import { uploadToS3 } from "@/utils";
import {
  createOrganisationStepOneSchema,
  createOrganisationStepTwoSchema,
  createOrganisationStepThreeSchema,
} from "@/utils/schema/createOrganisationSchema";
import { useDispatch } from "react-redux";
import { setPrimaryColor } from "@/store/slices";
import { TenantSetupTracker } from "@/components/tenants/profile/SetupTracker";
import {
  appOnBoardingIcon,
  brandingIcon,
  CampaignIcon,
  organizastionIcon,
} from "@/lib/Images";
const initAlertMsg = {
  open: false,
  label: "",
  msg: "",
  isError: false,
  isLoading: false,
  btnText: "",
  canClose: false,
};
const industryOptions = [
  "Manufacturing",
  "Healthcare",
  "Education",
  "Technology",
  "Cybersecurity",
  "Finance & Banking",
  "Energy & Utilities",
  "Retail & Consumer",
  "Government & Public Sector",
  "Professional Services",
];

const stepFields = {
  1: [
    "organisationName",
    "brandUsername",
    "industryType",
    "contactPerson",
    "contactEmail",
    "contactNumber",
    "addressLine1",
    "city",
    "state",
    "zip",
    "website",
    "deepLink",
  ],
  2: ["uploadLogo", "primaryColor", "secondaryColor"],
  3: [
    "campaignName",
    "campaignDescription",
    "campaignAccessType",
    "allowedDomains",
    "invitedEmails",
    // "joinCode",
  ],
};

export default function CreateOrganisation() {
  const navigate = useNavigate();
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      sessionStorage.setItem("isReload", "true");

      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    if (sessionStorage.getItem("isReload") === "true") {
      sessionStorage.removeItem("isReload");
      navigate("/tenants");
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate]);
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [logoFileId, setLogoFileId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [openAddExperience, setOpenAddExperience] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [protocol, setProtocol] = useState("https://");
  const [domainInput, setDomainInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [showTenantSuccessModal, setShowTenantSuccessModal] = useState(false);
  const [createdTenant, setCreatedTenant] = useState(null);
  const [brandingCompleted, setBrandingCompleted] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [setupStep, setSetupStep] = useState(0);

  const [createTenant, { isLoading }] = useCreateTenantMutation();
  const [updateTenantBranding] = useUpdateTenantBrandingMutation();
  const { data: presignedUrlData } = useGetPresignedURLQuery(
    {
      tenantId,
      fileName: file?.name,
      contentType: file?.type,
    },
    {
      skip: !(file instanceof File),
    },
  );
  const handleAlertClose = () => {
    setAlertMsg(initAlertMsg);
  };
  const form = useForm({
    defaultValues: {
      organisationName: "",
      brandUsername: "",
      industryType: "",
      contactPerson: "",
      contactEmail: "",
      contactNumber: "",
      addressLine1: "",
      city: "",
      state: "",
      zip: "",
      website: "",
      deepLink: "",
      uploadLogo: null,
      primaryColor: "", //#4F46E5
      secondaryColor: "#00D7F2",

      campaignName: "",
      campaignDescription: "",
      campaignAccessType: "INVITE_ONLY",
      allowedDomains: [],
      invitedEmails: [],
      // joinCode: "ACME-2026-01",
    },
  });

  const primaryPickerRef = useRef(null);
  const secondaryPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        primaryPickerRef.current &&
        !primaryPickerRef.current.contains(e.target)
      ) {
        setShowPrimaryPicker(false);
      }

      if (
        secondaryPickerRef.current &&
        !secondaryPickerRef.current.contains(e.target)
      ) {
        setShowSecondaryPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const primaryColor = form.watch("primaryColor");
  const secondaryColor = form.watch("secondaryColor");
  const campaignAccessType = form.watch("campaignAccessType");
  const allowedDomains = form.watch("allowedDomains");
  const deepLink = form.watch("deepLink");

  const dispatch = useDispatch();
  // useEffect(() => {
  //   if (primaryColor) {
  //     dispatch(setPrimaryColor(primaryColor));
  //   }
  // }, [primaryColor, dispatch]);

  const getCurrentStepSchema = () => {
    if (step === 1) return createOrganisationStepOneSchema;
    if (step === 2) return createOrganisationStepTwoSchema;
    return createOrganisationStepThreeSchema;
  };

  const setZodErrors = (error) => {
    const issues = error?.issues || error?.errors || [];

    issues.forEach((issue) => {
      const fieldName = issue?.path?.[0];

      if (!fieldName) return;

      form.setError(fieldName, {
        type: "manual",
        message: issue.message,
      });
    });
  };

  const validateCurrentStep = (values) => {
    const schema = getCurrentStepSchema();
    const result = schema.safeParse(values);

    form.clearErrors(stepFields[step]);

    if (!result.success) {
      setZodErrors(result.error);
      return false;
    }

    return true;
  };

  const handleBack = () => {
    console.log("current step", step);
    // if (step === 1) {
    navigate("/tenants");
    //   return;
    // }

    // setStep((prev) => prev - 1);
  };

  const saveStepOne = async (values) => {
    const isValid = validateCurrentStep(values);
    if (!isValid) return;
    setIsSaving(true);
    const formattedWebsite = values.website
      ? values.website.startsWith("www.")
        ? values.website
        : `www.${values.website}`
      : "";
    try {
      const payload = {
        orgName: values.organisationName,
        brandUsername: values.brandUsername,
        industryType: values.industryType,
        contactPerson: values.contactPerson,
        email: values.contactEmail,
        contactNumber: values.contactNumber,
        address: values.addressLine1,
        city: values.city,
        state: values.state,
        zip: values.zip,
        organisationAddress: values.addressLine1,
        website: formattedWebsite ? `${protocol}${formattedWebsite}` : "",
        deeplinkText: values.deepLink,
      };

      // const response = await fakeApiCall(payload);
      const response = await createTenant(payload).unwrap();

      if (response?.response?.statusEnum === 1) {
        const tenant = response?.response?.data;
        setTenantId(tenant?._id);
        setCreatedTenant(tenant);
        //  setSetupStep(tenant?.setupStep);
        setSetupStep(Math.max(Number(tenant?.setupStep) || 1, 1));
        // setTenantId(response?.response?.data?._id);
        setShowTenantSuccessModal(true);
        // setStep(2);
      } else {
        setAlertMsg({
          open: true,
          label: "Tenant Creation Failed",
          msg:
            response?.response?.message ||
            "Unable to create tenant. Please try again.",
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: "Tenant Creation Failed",
        msg:
          err?.data?.response?.data?.join(", ") ||
          err?.data?.response?.message ||
          err?.data?.message ||
          "Something went wrong. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveStepTwo = async (values) => {
    const isValid = validateCurrentStep(values);

    if (!isValid) return;

    setIsSaving(true);

    try {
      const payload = {
        logoUrl: logoFileId,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
      };
      const response = await updateTenantBranding({
        tenantId,
        payload,
      }).unwrap();

      if (response?.response?.statusEnum === 1) {
        const responseSetupStep = Number(
          response?.response?.data?.setupStep ??
            response?.response?.data?.tenant?.setupStep ??
            2,
        );

        setSetupStep((previousStep) =>
          Math.max(previousStep, responseSetupStep),
        );
        const latestPrimaryColor =
          response?.response?.data?.branding?.primaryColor ||
          response?.response?.data?.primaryColor ||
          values.primaryColor;

        dispatch(
          setPrimaryColor({
            tenantId,
            primaryColor: latestPrimaryColor,
          }),
        );
        localStorage.setItem(`tenant-primary-colors-${tenantId}`, latestPrimaryColor);

        // dispatch(setPrimaryColor(values.primaryColor));
        setShowBrandingModal(true);
        // setBrandingCompleted(true);
        // setShowSuccessModal(true);
        // setStep(3);
      }
    } finally {
      setIsSaving(false);
    }
  };
  const [createCampaign] = useCreateCampaignMutation();

  const saveStepThree = async (values) => {
    const isValid = validateCurrentStep(values);

    if (!isValid) return;

    setIsSaving(true);

    try {
      const payload = {
        tenantId,
        campaignName: values.campaignName,
        campaignDescription: values.campaignDescription,
        campaignAccessType: values.campaignAccessType || "INVITE_ONLY",
        allowedDomains: values.allowedDomains,
        // invitedEmails:
        //   values.campaignAccessType === "INVITE_ONLY"
        //     ? values.invitedEmails
        //     : [],
      };

      const res = await createCampaign(payload).unwrap();

      // console.log("Campaign created:", res);

      if (res?.response?.statusEnum === 1) {
        const responseSetupStep = Number(
          res?.response?.data?.setupStep ??
            res?.response?.data?.tenant?.setupStep ??
            0,
        );

        if (responseSetupStep > 0) {
          setSetupStep((previousStep) =>
            Math.max(previousStep, responseSetupStep),
          );
        }
        setShowCampaignModal(true);
        // setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Create campaign error:", err);

      setAlertMsg({
        open: true,
        label: "Campaign Creation Failed",
        msg:
          err?.data?.response?.message ||
          "Something went wrong. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCurrentStepSubmit = async (values) => {
    if (step === 1) {
      await saveStepOne(values);
      return;
    }

    if (step === 2) {
      await saveStepTwo(values);
      return;
    }

    await saveStepThree(values);
  };

  const addDomain = () => {
    const value = domainInput.trim().replace(",", "");

    if (!value) return;

    const domainRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

    if (!domainRegex.test(value)) {
      form.setError("allowedDomains", {
        type: "manual",
        message: "Please enter a valid domain",
      });

      return;
    }

    const currentDomains = form.getValues("allowedDomains") || [];

    if (!currentDomains.includes(value)) {
      form.setValue("allowedDomains", [...currentDomains, value], {
        shouldValidate: true,
        shouldDirty: true,
      });

      form.clearErrors("allowedDomains");
    }

    setDomainInput("");
  };
  const removeDomain = (domain) => {
    const currentDomains = form.getValues("allowedDomains") || [];
    form.setValue(
      "allowedDomains",
      currentDomains.filter((item) => item !== domain),
      { shouldValidate: true, shouldDirty: true },
    );
  };
  const addInvitedEmail = () => {
    const value = emailInput.trim().replace(",", "");

    if (!value) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      form.setError("invitedEmails", {
        type: "manual",
        message: "Please enter a valid email",
      });

      return;
    }

    const currentEmails = form.getValues("invitedEmails") || [];

    if (!currentEmails.includes(value)) {
      form.setValue("invitedEmails", [...currentEmails, value], {
        shouldValidate: true,
        shouldDirty: true,
      });

      form.clearErrors("invitedEmails");
    }

    setEmailInput("");
  };

  const removeInvitedEmail = (email) => {
    const currentEmails = form.getValues("invitedEmails") || [];
    form.setValue(
      "invitedEmails",
      currentEmails.filter((item) => item !== email),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  useEffect(() => {
    const uploadLogo = async () => {
      const uploadUrl = presignedUrlData?.response?.data?.uploadUrl;

      const objectUrl = presignedUrlData?.response?.data?.objectUrl;

      if (file && uploadUrl) {
        try {
          const success = await uploadToS3(file, uploadUrl);

          if (!success) {
            throw new Error("S3 upload failed");
          }

          setLogoFileId(objectUrl);
        } catch (err) {
          console.error("Upload failed", err);
        }
      }
    };

    uploadLogo();
  }, [file, presignedUrlData]);

  const normalizedSetupStep = Math.min(Math.max(Number(setupStep) || 0, 0), 4);

  const tenantCreationSteps = [
    {
      id: 1,
      label: "Organisation Setup",
      completed: normalizedSetupStep >= 1,
      icon: (
        <img src={organizastionIcon} alt="Organization" className="h-6 w-6" />
      ),
    },
    {
      id: 2,
      label: "Add Branding",
      completed: normalizedSetupStep >= 2,
      icon: <img src={brandingIcon} alt="Branding" className="h-7 w-7" />,
    },
    {
      id: 3,
      label: "Setup App Onboarding",
      completed: normalizedSetupStep >= 3,
      icon: (
        <img src={appOnBoardingIcon} alt="Onboarding" className="h-7 w-7" />
      ),
    },
    {
      id: 4,
      label: "Create Campaign & Add Experience",
      completed: normalizedSetupStep >= 4,
      icon: <img src={CampaignIcon} alt="Campaign" className="h-7 w-7" />,
    },
  ];

  const completedTenantCreationSteps = tenantCreationSteps.filter(
    (item) => item.completed,
  ).length;

  const tenantCreationProgress = Math.round(
    (completedTenantCreationSteps / tenantCreationSteps.length) * 100,
  );

  return (
    <div className="min-h-screen  px-6 py-6 text-neutral-100">
      <div className="max-w-[720px]">
        <Button
          type="button"
          variant="neutral"
          onClick={handleBack}
          className="mb-7 rounded-md border border-brand-20  text-xs font-semibold text-[#7EDBFF] hover:bg-white/5"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleCurrentStepSubmit)}>
            {step === 1 && (
              <StepOne
                form={form}
                protocol={protocol}
                setProtocol={setProtocol}
              />
            )}

            {step === 2 && (
              <StepTwo
                form={form}
                file={file}
                setFile={setFile}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                showPrimaryPicker={showPrimaryPicker}
                showSecondaryPicker={showSecondaryPicker}
                setShowPrimaryPicker={setShowPrimaryPicker}
                setShowSecondaryPicker={setShowSecondaryPicker}
                primaryPickerRef={primaryPickerRef}
                secondaryPickerRef={secondaryPickerRef}
                // dispatch={dispatch}
              />
            )}

            {step === 3 && (
              <StepThree
                form={form}
                tenantId={tenantId}
                campaignAccessType={campaignAccessType}
                allowedDomains={allowedDomains}
                domainInput={domainInput}
                setDomainInput={setDomainInput}
                addDomain={addDomain}
                removeDomain={removeDomain}
                emailInput={emailInput}
                setEmailInput={setEmailInput}
                addInvitedEmail={addInvitedEmail}
                removeInvitedEmail={removeInvitedEmail}
              />
            )}

            <div className="mt-8 flex justify-end gap-4">
              <Button
                type="button"
                variant="neutral"
                onClick={handleBack}
                className="h-11 min-w-[110px] border-[#607A9B] bg-[#223957] text-neutral-100 hover:bg-[#2B466B]"
              >
                {step === 1 ? "Cancel" : "Back"}
              </Button>

              <Button
                type="submit"
                disabled={isSaving}
                className="h-11 min-w-[130px] bg-brand-21 font-bold text-neutral-100 hover:bg-brand-22"
              >
                {isSaving
                  ? "Saving..."
                  : step === 1
                    ? "Create Tenant"
                    : step === 2
                      ? "Save"
                      : "Save & Finish"}
              </Button>
            </div>
          </form>
        </Form>
      </div>

      <AddExperienceDialog
        open={openAddExperience}
        onClose={() => setOpenAddExperience(false)}
        tenantName={form.watch("organisationName")}
        defaultCampaign={form.watch("campaignName")}
        deepLink={deepLink}
        onSuccess={(response) => {
          const responseSetupStep = Number(
            response?.response?.data?.setupStep ??
              response?.data?.setupStep ??
              4,
          );

          setSetupStep(Math.max(responseSetupStep, 4));
          setOpenAddExperience(false);
        }}
      />

      <SuccessModal
        open={showCampaignModal}
        onClose={() => {
          setShowCampaignModal(false);
          navigate("/tenants");
        }}
        onAddExperience={() => {
          setShowCampaignModal(false);
          setOpenAddExperience(true);
        }}
        // onAddCampaign={() => {
        //   setShowSuccessModal(false);
        //   form.setValue("campaignName", "");
        //   form.setValue("campaignDescription", "");
        //   form.setValue("campaignAccessType", "");
        //   form.setValue("allowedDomains", []);
        //   setStep(3);
        // }}
        onAddCampaign={() => {
          setShowCampaignModal(false);
          setStep(3);
        }}
      >
        <TenantSetupTracker
          progress={tenantCreationProgress}
          steps={tenantCreationSteps}
        />
      </SuccessModal>

      {/* <SuccessModal
        open={showTenantSuccessModal}
        onClose={() => setShowTenantSuccessModal(false)}
        title="Tenant Created Successfully"
        description="The tenant organisation has been set up successfully. You can add branding details now or do it later from the tenant profile."
        primaryButtonText="Add Branding"
        secondaryButtonText="Go to Tenant Profile"
        iconType="success"
        onPrimaryClick={() => {
          setShowTenantSuccessModal(false);
          setStep(2);
        }}
        onSecondaryClick={() => {
          navigate(`/tenants`);
        }}
      /> */}

      <SuccessModal
        open={showTenantSuccessModal}
        onClose={() => setShowTenantSuccessModal(false)}
        title="Tenant Created Successfully"
        description="The tenant organisation has been set up successfully. You can add branding details now or do it later from the tenant profile."
        primaryButtonText="Add Branding"
        secondaryButtonText="Go to Tenant Profile"
        showIcon={false}
        iconType="success"
        onPrimaryClick={() => {
          setShowTenantSuccessModal(false);
          setStep(2);
        }}
        onSecondaryClick={() => {
          navigate("/tenants/profile", {
            state: {
              tenantId,
            },
            replace: true,
          });
        }}
      >
        <TenantSetupTracker
          progress={tenantCreationProgress}
          steps={tenantCreationSteps}
        />
      </SuccessModal>

      <SuccessModal
        open={showBrandingModal}
        onClose={() => setShowBrandingModal(false)}
        title="Branding Saved"
        description="The branding for this tenant has been successfully configured and will be applied across the campaign and experience."
        primaryButtonText="Setup Application Onboarding"
        secondaryButtonText="Go to Tenant Profile"
        showIcon={false}
        iconType="success"
        onPrimaryClick={() => {
          setShowBrandingModal(false);
          navigate("/tenants/profile", {
            state: {
              tenantId,
              activeTab: "onboarding",
            },
            replace: true,
          });
        }}
        onSecondaryClick={() => {
          navigate(`/tenants`);
        }}
      >
        <TenantSetupTracker
          progress={tenantCreationProgress}
          steps={tenantCreationSteps}
        />
      </SuccessModal>
      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
}

const StepOne = ({ form, protocol, setProtocol }) => {
  return (
    <div>
      <h1 className="mb-2 text-2xl font-bold text-[#67D8FF]">
        Step 1: Create Organisation
      </h1>

      <p className="mb-7 text-sm text-neutral-100/70">
        Set up a new organisation to manage campaigns, users, and experiences.
      </p>

      <FormField
        control={form.control}
        name="organisationName"
        render={({ field, fieldState }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Organisation Name
            </FormLabel>

            <FormControl>
              <Input
                placeholder="Enter organisation name (e.g. Acme Corp)"
                className={cn(
                  "form-dark-input",
                  fieldState.error &&
                    "border-red-500 focus-visible:ring-red-500",
                )}
                {...field}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="brandUsername"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Brand Username
            </FormLabel>

            <FormControl>
              <Input
                placeholder="Enter brand username"
                className="form-dark-input"
                {...field}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s/g, "");

                  field.onChange(value);
                }}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="industryType"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Industry Type
            </FormLabel>

            <FormControl>
              <CustomSelect
                value={field.value}
                placeholder="Select an industry"
                options={industryOptions}
                onChange={field.onChange}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contactPerson"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Contact Person
            </FormLabel>

            <FormControl>
              <Input
                placeholder="Enter full name (e.g. John Doe)"
                className="form-dark-input"
                {...field}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="contactEmail"
          render={({ field }) => (
            <FormItem className="mb-5">
              <FormLabel className="text-sm font-bold text-neutral-100">
                Contact Email
              </FormLabel>

              <FormControl>
                <Input
                  placeholder="Enter email address (e.g. john@acme.com)"
                  className="form-dark-input"
                  {...field}
                />
              </FormControl>

              <FormMessage className="text-sm text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contactNumber"
          render={({ field }) => (
            <FormItem className="mb-5">
              <FormLabel className="text-sm font-bold text-neutral-100">
                Contact Number
              </FormLabel>

              <FormControl>
                <Input
                  placeholder="Enter phone number (e.g. +1 234 567 8900)"
                  className="form-dark-input"
                  {...field}
                />
              </FormControl>

              <FormMessage className="text-sm text-red-500" />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="addressLine1"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Organisation Address
            </FormLabel>

            <FormControl>
              <Input
                placeholder="Address line 1"
                className="form-dark-input"
                {...field}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="City"
                  className="form-dark-input"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-sm text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="State"
                  className="form-dark-input"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-sm text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="zip"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Zip"
                  className="form-dark-input"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-sm text-red-500" />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="website"
        render={({ field }) => (
          <FormItem className="mt-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Website (Optional)
            </FormLabel>

            <FormControl>
              <div className="flex items-center gap-2">
                <select
                  value={protocol}
                  onChange={(e) => setProtocol(e.target.value)}
                  className="h-10 rounded-md border border-neutral-700 bg-brand-10 px-2 text-sm text-neutral-100"
                  style={{ width: "auto" }}
                >
                  <option value="https://">https:// </option>
                  <option value="http://">http://</option>
                </select>
                <Input
                  placeholder="Enter website URL (e.g. www.acme.com)"
                  className="form-dark-input"
                  {...field}
                />
              </div>
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="deepLink"
        render={({ field }) => (
          <FormItem className="mt-6">
            <div className="rounded-[20px] border border-[#56C7FF] bg-[#17385B]/70 p-5">
              <FormLabel className="mb-3 block text-xl font-bold text-[#67D8FF]">
                Enter the deeplink provided for this tenant. (Optional)
              </FormLabel>

              <p className="mb-5 text-sm leading-7 text-neutral-100/70">
                Add a tenant-specific deeplink that employees can use to access
                their assigned simulation experience directly. This link can be
                shared through email, chat, internal portals, or onboarding
                documents.
              </p>

              <FormControl>
                <Input
                  placeholder="eg- https://example.com/access/tenant-name"
                  className="form-dark-input h-12 rounded-xl px-4 text-sm"
                  {...field}
                />
              </FormControl>

              <FormMessage className="mt-2 text-sm text-red-500" />
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};

const StepTwo = ({
  form,
  setFile,
  primaryColor,
  secondaryColor,
  showPrimaryPicker,
  showSecondaryPicker,
  setShowPrimaryPicker,
  setShowSecondaryPicker,
  primaryPickerRef,
  secondaryPickerRef,
  // dispatch,
}) => {
  const dispatch = useDispatch();
  // useEffect(() => {
  //   dispatch(setPrimaryColor(primaryColor));
  // }, [primaryColor]);

  const validateImageRatio = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const isSquare = img.width === img.height;

        URL.revokeObjectURL(objectUrl);
        resolve(isSquare);
      };

      img.src = objectUrl;
    });
  };

  return (
    <div>
      <h1 className="mb-2 text-2xl font-bold text-[#67D8FF]">
        Step 2: Add Branding
      </h1>

      <p className="mb-7 text-sm text-neutral-100/70">
        Setup how this organisation’s experiences will appear across the
        platform.
      </p>

      <FormField
        control={form.control}
        name="uploadLogo"
        render={({ field, fieldState }) => (
          <FormItem className="mb-5">
            {/* <FormLabel className="text-sm font-bold text-neutral-100">
              Upload Logo
            </FormLabel> */}

            <FormControl>
              <UploadsImagesWithPreview
                placeholder="Upload logo"
                fileFormat="Upload logo (PNG, JPG)"
                onChange={(val) => {
                  field.onChange(val);
                  setFile(val);

                  if (val) {
                    form.clearErrors("uploadLogo");
                  }
                }}
                // onChange={(val) => {
                //   setFile(val);
                //   if (val) setError("");
                // }}
                // onChange={async (val) => {
                //   if (val instanceof File) {
                //     const isValidRatio = await validateImageRatio(val);

                //     if (!isValidRatio) {
                //       form.setError("uploadLogo", {
                //         type: "manual",
                //         message: "Only 1:1 ratio image is allowed.",
                //       });

                //       return;
                //     }
                //   }

                //   field.onChange(val);
                //   setFile(val);

                //   if (val) {
                //     form.clearErrors("uploadLogo");
                //   }
                // }}
                value={field.value}
                setError={(msg) =>
                  form.setError("uploadLogo", {
                    type: "manual",
                    message: msg,
                  })
                }
              />
            </FormControl>

            {/* {fieldState.error && (
              <p className="mt-1 text-sm text-red-500">
                {fieldState.error.message}
              </p>
            )} */}

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="primaryColor"
        render={({ field }) => (
          <FormItem className="relative mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Add Primary Color
            </FormLabel>

            <p className="mb-3 text-sm text-neutral-100/60">
              This is the main color and the identity of a brand. It will be
              used across key areas like CTAs and highlights.
            </p>

            <FormControl>
              <ColorInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Select from palette or enter Hex code"
                onClick={() => {
                  setShowPrimaryPicker((prev) => !prev);
                  setShowSecondaryPicker(false);
                }}
              />
            </FormControl>

            {/* {showPrimaryPicker && (
              <div className="custom-sketch-picker z-20 mt-3 w-fit ml-auto sm:absolute sm:right-0 sm:top-full sm:mt-3 xl:right-auto xl:left-[760px] xl:top-[-150px] xl:mt-0"> */}
            {/* <ChromePicker
                  color={primaryColor}
                  onChange={(color) => field.onChange(color.hex)}
                /> */}
            {/* <SketchPicker
                  color={primaryColor}
                  onChange={(color) => field.onChange(color.hex)}
                />
              </div>
            )} */}
            {showPrimaryPicker && (
              <div
                ref={primaryPickerRef}
                className="custom-sketch-picker z-20 mt-3 w-fit ml-auto sm:absolute sm:right-0 sm:top-full sm:mt-3 xl:right-auto xl:left-[760px] xl:top-[-150px] xl:mt-0"
              >
                <SketchPicker
                  color={primaryColor}
                  onChange={(color) => {
                    field.onChange(color.hex);
                    // dispatch(setPrimaryColor(color.hex));
                  }}
                />
              </div>
            )}
            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      {/* <FormField
        control={form.control}
        name="secondaryColor"
        render={({ field }) => (
          <FormItem className="relative mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Add Secondary / Accent Color Optional
            </FormLabel>

            <p className="mb-3 text-sm text-neutral-100/60"> */}
      {/* This color is there to accent, distinguish and support the primary
              brand color. */}
      {/* This color is there to accent, distinguish and support the primary
              brand color. It is used to highlight important elements like
              actions, links and interactive states.
            </p> */}

      {/* <FormControl>
              <ColorInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Select from palette or enter Hex code"
                onClick={() => {
                  setShowSecondaryPicker((prev) => !prev);
                  setShowPrimaryPicker(false);
                }}
              />
            </FormControl> */}

      {/* {showSecondaryPicker && (
              <div className="custom-sketch-picker z-20 mt-3 w-fit ml-auto sm:absolute sm:right-0 sm:top-full sm:mt-3 xl:right-auto xl:left-[760px] xl:top-[-170px] xl:mt-0"> */}
      {/* <ChromePicker
                  color={secondaryColor}
                  onChange={(color) => field.onChange(color.hex)}
                /> */}
      {/* <SketchPicker
                  color={secondaryColor}
                  onChange={(color) => field.onChange(color.hex)}
                />
              </div>
            )} */}
      {/* {showSecondaryPicker && (
              <div
                ref={secondaryPickerRef}
                className="custom-sketch-picker z-20 mt-3 w-fit ml-auto sm:absolute sm:right-0 sm:top-full sm:mt-3 xl:right-auto xl:left-[760px] xl:top-[-170px] xl:mt-0"
              >
                <SketchPicker
                  color={secondaryColor}
                  onChange={(color) => field.onChange(color.hex)}
                />
              </div>
            )}

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      /> */}
    </div>
  );
};

const StepThree = ({
  form,
  tenantId,
  campaignAccessType,
  allowedDomains,
  domainInput,
  setDomainInput,
  addDomain,
  removeDomain,
  emailInput,
  setEmailInput,
  addInvitedEmail,
  removeInvitedEmail,
}) => {
  return (
    <div>
      <h1 className="mb-2 text-2xl font-bold text-[#67D8FF]">
        Step 3: Create New Campaign
      </h1>

      {/* <p className="mb-7 text-sm text-neutral-100/70">
        Create Campaigns to help organise experiences and assign users within an
        organisation.
      </p> */}

      <FormField
        control={form.control}
        name="campaignName"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Campaign Name
            </FormLabel>

            <FormControl>
              <Input
                placeholder="Enter campaign name (e.g. Cyber Awareness Q2)"
                className="form-dark-input"
                {...field}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="campaignDescription"
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Description
            </FormLabel>

            <p className="mb-3 text-sm text-neutral-100/60">
              Enter a short description of this campaign
            </p>

            <FormControl>
              <textarea
                {...field}
                className="min-h-[90px] w-full rounded-md border border-[#14708A] bg-[#151515] px-4 py-3 text-sm text-neutral-100 outline-none placeholder:text-neutral-100/50 focus:border-brand-15"
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      {/* Campaign Access Type */}
      <FormField
        control={form.control}
        name="campaignAccessType"
        rules={{ required: "Campaign access type is required" }}
        render={({ field }) => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Campaign Access Type
            </FormLabel>

            <FormControl>
              <CampaignAccessSelect
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>

            <FormMessage className="text-sm text-red-500" />
          </FormItem>
        )}
      />

      {/* Allowed Domains */}
      <FormField
        control={form.control}
        name="allowedDomains"
        render={() => (
          <FormItem className="mb-5">
            <FormLabel className="text-sm font-bold text-neutral-100">
              Invite users via adding domain addresses
            </FormLabel>

            <p className="mb-3 text-sm text-neutral-100/60">
              Allow access to anyone who signs in with an email from{" "}
              <span className="font-bold text-neutral-100/80">
                specific domains
              </span>
              . Type domains and press Enter to add multiple domains.
            </p>

            <FormControl>
              <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-[#14708A] bg-[#151515] px-3 py-2">
                {allowedDomains?.map((domain) => (
                  <span
                    key={domain}
                    className="flex items-center gap-2 rounded bg-[#293C79] px-2 py-1 text-xs font-semibold text-neutral-100"
                  >
                    {domain}

                    <button
                      type="button"
                      onClick={() => removeDomain(domain)}
                      className="text-neutral-100/80 hover:text-neutral-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                <input
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addDomain();
                    }

                    if (
                      e.key === "Backspace" &&
                      !domainInput &&
                      allowedDomains?.length > 0
                    ) {
                      removeDomain(allowedDomains[allowedDomains.length - 1]);
                    }
                  }}
                  onBlur={addDomain}
                  placeholder={
                    allowedDomains?.length ? "" : "Enter domain and press Enter"
                  }
                  className="min-w-[150px] flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-100/50"
                />
              </div>
            </FormControl>

            {Array.isArray(form.formState.errors.allowedDomains) &&
              form.formState.errors.allowedDomains.map((err, index) => {
                if (!err) return null;

                return (
                  <p key={index} className="text-sm text-red-500">
                    {err.message}
                  </p>
                );
              })}

            {form.formState.errors.allowedDomains?.message && (
              <p className="text-sm text-red-500">
                {form.formState.errors.allowedDomains.message}
              </p>
            )}
          </FormItem>
        )}
      />

      {/* Invite Emails */}
      {/* {form.watch("campaignAccessType") === "INVITE_ONLY" && (
        <FormField
          control={form.control}
          name="invitedEmails"
          render={({ field }) => (
            <FormItem className="mb-5">
              <FormLabel className="text-sm font-bold text-neutral-100">
                Invited Emails
              </FormLabel>

              <FormControl>
                <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-[#14708A] bg-[#151515] px-3 py-2">
                  {field.value?.map((email) => (
                    <span
                      key={email}
                      className="flex items-center gap-2 rounded bg-[#293C79] px-2 py-1 text-xs font-semibold text-neutral-100"
                    >
                      {email}

                      <button
                        type="button"
                        onClick={() => removeInvitedEmail(email)}
                        className="text-neutral-100/80 hover:text-neutral-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  <input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addInvitedEmail();
                      }

                      if (
                        e.key === "Backspace" &&
                        !emailInput &&
                        field.value?.length > 0
                      ) {
                        removeInvitedEmail(field.value[field.value.length - 1]);
                      }
                    }}
                    onBlur={addInvitedEmail}
                    placeholder={
                      field.value?.length ? "" : "Enter email and press Enter"
                    }
                    className="min-w-[180px] flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-100/50"
                  />
                </div>
              </FormControl>

              {Array.isArray(form.formState.errors.invitedEmails) &&
                form.formState.errors.invitedEmails.map((err, index) => {
                  if (!err) return null;

                  return (
                    <p key={index} className="text-sm text-red-500">
                      {err.message}
                    </p>
                  );
                })}

              {form.formState.errors.invitedEmails?.message && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.invitedEmails.message}
                </p>
              )}
            </FormItem>
          )}
        />
      )} */}
    </div>
  );
};
